/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 * 
 * Design Tokens - Centralized design system values
 * These tokens should be used across all styling approaches (SCSS, styled-components, inline styles)
 */

export const tokens = {
  colors: {
    // Base colors
    white: '#fff',
    black: '#000',
    
    // Grays
    'gray-100': '#f8f9fa',
    'gray-200': '#e9ecef',
    'gray-300': '#dee2e6',
    'gray-400': '#ced4da',
    'gray-500': '#adb5bd',
    'gray-600': '#6c757d',
    'gray-700': '#495057',
    'gray-800': '#343a40',
    'gray-900': '#212529',
    'gray-card': '#A6A6A6',
    
    // Brand colors
    blue: '#2d5d7e',
    'power-blue': '#2c354f',
    'tech-blue': '#2d5d7e',
    
    // Semantic colors
    primary: '#2d5d7e',
    secondary: '#2d5d7e', // tech-blue
    success: '#6da06f',
    info: '#ee5a38',
    warning: '#fdba43',
    danger: '#c13727',
    light: '#f8f9fa', // gray-100
    dark: '#343a40', // gray-800
    
    // Extended palette
    indigo: '#6610f2',
    purple: '#6f42c1',
    pink: '#e83e8c',
    red: '#c13727',
    orange: '#ee5a38',
    yellow: '#fdba43',
    green: '#6da06f',
    teal: '#20c997',
    cyan: '#17a2b8',
  },
  
  spacing: {
    // Accordion specific
    accordionPadding: '0.25em 0.5em 0.25em 0.7em',
    accordionContentPadding: '6px',
    
    // Standard spacing scale
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    xxl: '3rem',     // 48px
  },
  
  typography: {
    // Font sizes
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
    },

    // Font weights
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },

    /**
     * Heading hierarchy
     * ------------------------------------------------------------------
     * Use weight to reinforce nesting depth. Weight should be monotonic
     * non-increasing as you go deeper — a child must never be heavier
     * than its parent. Small action labels (toggles, callouts) cap at
     * `semibold` even at xs size, so they don't compete with structural
     * titles. Color, letter-spacing, and case can carry the rest of the
     * emphasis.
     *
     *   Level 1 — Page / panel heading        size: lg   weight: bold      (700)
     *   Level 2 — Card / section title        size: sm   weight: semibold  (600)
     *   Level 3 — Subgroup / nested section   size: xs   weight: medium    (500)
     *   Level 4 — Field / leaf label          size: xs   weight: medium    (500)
     *   Action labels (e.g. OPTIONS, ADVANCED) size: xs   weight: semibold (600)
     *   Callout titles (e.g. InsightBox)      size: xs   weight: semibold  (600)
     */
    hierarchy: {
      pageHeading:    { size: '1.125rem', weight: 700 },
      cardTitle:      { size: '0.875rem', weight: 600 },
      subgroupTitle:  { size: '0.75rem',  weight: 500 },
      fieldLabel:     { size: '0.75rem',  weight: 500 },
      actionLabel:    { size: '0.75rem',  weight: 600 },
      calloutTitle:   { size: '0.75rem',  weight: 600 },
    },
  },
  
  // Transitions
  transitions: {
    fast: '0.15s ease-in-out',
    base: '0.3s ease-in-out',
    slow: '0.5s ease-in-out',
    accordion: '0.3s cubic-bezier(1, 0, 1, 0)',
    accordionCollapse: '0.35s cubic-bezier(0, 1, 0, 1)',
  },
  
  // Shadows
  shadows: {
    sm: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 0px',
    md: '0 2px 4px rgba(0, 0, 0, 0.1)',
    lg: '0 4px 6px rgba(0, 0, 0, 0.1)',
    sidebar: 'rgba(0, 0, 0, 0.15) 0px 2px 8px', // For sidebar/filters pane
  },
  
  // Borders
  borders: {
    width: {
      thin: '1px',
      base: '2px',
      thick: '4px',
    },
    radius: {
      sm: '0.25rem',
      md: '0.5rem',
      lg: '1rem',
      full: '9999px',
    },
  },
  
  // Z-index scale
  zIndex: {
    base: 1,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
  
  // Responsive breakpoints (matches Bootstrap 5 defaults)
  breakpoints: {
    xs: '0px',      // Extra small devices (portrait phones)
    sm: '576px',    // Small devices (landscape phones)
    md: '768px',    // Medium devices (tablets)
    lg: '992px',    // Large devices (desktops)
    xl: '1200px',   // Extra large devices (large desktops)
    xxl: '1400px',  // XXL devices (larger desktops)
  },
};

// Export for use in SCSS (via JS-to-SCSS bridge if needed)
export default tokens;

